/**
 * SkillCard — single tile in the Skills catalog grid.
 *
 * Notion-strict layout (per UI_GUIDELINES_v1):
 *   - 13px skill name (semibold) + key + version on the left
 *   - 11px description (line-clamp-2) underneath
 *   - effectiveness dot + percentage on the right
 *   - hairline border, rounded-4px, hover bg-muted/40
 *   - kebab menu hidden until hover, opens link to detail page
 *
 * Click on the body navigates to /university/skills/$skillId. The
 * effectiveness colour (green/yellow/red) is the only saturated cue —
 * everything else stays monochrome per ADR-057.
 */
import type { SkillCatalogEntry } from '@chaos/types/learning';
import { Link } from '@tanstack/react-router';

import { cn } from '../../lib/utils';
import {
  effectivenessTone,
  fmtCount,
  fmtRelative,
  fmtScore,
  MARKETPLACE_STATUS_LABEL,
  TONE_DOT_CLASS,
  TONE_TEXT_CLASS,
} from './formatters';

interface SkillCardProps {
  skill: SkillCatalogEntry;
}

export function SkillCard({ skill }: SkillCardProps) {
  const tone = effectivenessTone(skill.effectivenessScore);
  const statusLabel = MARKETPLACE_STATUS_LABEL[skill.marketplaceStatus] ?? skill.marketplaceStatus;
  return (
    <div className="group/skill relative flex h-full flex-col gap-2 rounded border border-border bg-background p-3 transition-colors hover:bg-muted/40">
      <Link
        to="/university/skills/$skillId"
        params={{ skillId: skill.skillId }}
        className="absolute inset-0 z-0 rounded"
        aria-label={`${skill.name} — ${statusLabel}`}
      />
      <header className="relative z-10 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-1.5">
            <span
              aria-hidden
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT_CLASS[tone])}
              title={`Effectiveness: ${fmtScore(skill.effectivenessScore)}`}
            />
            <span className="truncate text-[13px] font-semibold text-foreground">{skill.name}</span>
          </div>
          <span className="mt-0.5 truncate text-[11px] font-mono text-muted-foreground">
            {skill.key}
            <span className="opacity-50">
              {' · v'}
              {skill.version}
            </span>
          </span>
        </div>
        <span
          className={cn(
            'shrink-0 font-mono text-[13px] font-semibold tabular-nums',
            TONE_TEXT_CLASS[tone],
          )}
        >
          {fmtScore(skill.effectivenessScore)}
        </span>
      </header>

      {skill.description ? (
        <p className="relative z-10 line-clamp-2 text-[12px] text-muted-foreground">
          {skill.description}
        </p>
      ) : null}

      <footer className="relative z-10 mt-auto flex items-baseline gap-3 text-[11px] text-muted-foreground">
        <span>
          attempts{' '}
          <span className="font-medium text-foreground">{fmtCount(skill.totalAttempts)}</span>
        </span>
        <span aria-hidden>·</span>
        <span
          title={skill.lastExportAt ? `last export ${fmtRelative(skill.lastExportAt)}` : undefined}
        >
          exports <span className="font-medium text-foreground">{fmtCount(skill.exportCount)}</span>
        </span>
        <span aria-hidden>·</span>
        <span className="truncate" title={`status: ${statusLabel}`}>
          {statusLabel.toLowerCase()}
        </span>
      </footer>
    </div>
  );
}
